import { useEffect, useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import { inventoryStore } from '@/stores/inventory.store';
import { modalStore } from '@/stores/modal.store';
import { coinflipStore, coinflipIntent } from '@/stores/coinflip.store';
import CoinflipInventoryPicker from '@/components/Modals/Inventory/CoinflipInventoryPicker';
import CoinflipJoinFooter from '@/components/Modals/Inventory/CoinflipJoinFooter';
import Chip from '@/components/Coinflip/Chip';

const CreateCoinflip = () => {
    const {
        inventoryItems,
        selectedItemIds,
        maxSelectedItems,
        setSelectedById,
        clearInventory,
        loadSiteInventoryDeposit,
    } = inventoryStore((state) => state);
    const { setTitle, setComponent, setData } = modalStore((state) => state);
    const { minbet } = coinflipStore((state) => state);

    const [loading, setLoading] = useState(true);
    const [side, setSide] = useState<1 | 2>(1);

    useEffect(() => {
        loadSiteInventoryDeposit().finally(() => setLoading(false));
        return () => clearInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pickerItems = inventoryItems.map((it) => ({
        id: it.id,
        name: it.item.name,
        priceUsd: it.item.priceUsd,
        colorHex: it.item.colorHex,
        icon: it.item.icon,
        iconBackground: it.item.iconBackground,
    }));

    const inventoryTotal = pickerItems.reduce((s, i) => s + i.priceUsd, 0);
    const selected = pickerItems.filter((i) => selectedItemIds.has(i.id));
    const selectedAmount = selected.reduce((s, i) => s + i.priceUsd, 0);
    const canCreate = selected.length > 0 && selectedAmount >= minbet;

    async function create() {
        if (!canCreate) return;
        // Mark this tab as the initiator so the coinflipCreated broadcast only
        // opens the game here, not on the user's other open tabs.
        coinflipIntent.create = true;
        const inventoryIds = Array.from(selectedItemIds);
        const {
            data: {
                response: { gameId },
            },
        } = await ApiRequest.post('/coinflip/create/inventory', {
            inventoryIds,
            side,
        });
        if (gameId) {
            setTitle(`COINFLIP #${gameId}`);
            setComponent('View coinflip');
            setData({ id: gameId });
        }
    }

    return (
        <>
            {/* Side picker */}
            <div className="flex items-center justify-center gap-3 mb-4 pt-2">
                <span className="text-[11px] uppercase tracking-[0.1em] text-text_3 font-semibold">
                    Create as
                </span>
                <Chip
                    variant="red"
                    selected={side === 1}
                    onClick={() => setSide(1)}
                    aria-label="Side 1 — red chip"
                />
                <Chip
                    variant="black"
                    selected={side === 2}
                    onClick={() => setSide(2)}
                    aria-label="Side 2 — black chip"
                />
            </div>

            <CoinflipInventoryPicker
                items={pickerItems}
                selectedItemIds={selectedItemIds}
                setSelectedById={setSelectedById}
                maxItems={maxSelectedItems}
                loading={loading}
                onRefresh={() => {
                    setLoading(true);
                    clearInventory();
                    loadSiteInventoryDeposit().finally(() => setLoading(false));
                }}
                footer={
                    <CoinflipJoinFooter
                        inventoryTotal={inventoryTotal}
                        selectedCount={selected.length}
                        selectedAmount={selectedAmount}
                        minBet={minbet}
                        onAction={create}
                        actionLabel="CREATE"
                        actionDisabled={!canCreate}
                    />
                }
            />
        </>
    );
};

export default CreateCoinflip;
